import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Radio,
  Play,
} from "lucide-react";
import client from "../api/client";
import "./Admin.css";

/** Drive sync health: channel state, cursor, counts, and recent runs. */

const formatTime = (value) => {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
};

const relative = (value) => {
  if (!value) return null;
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
};

export default function SyncStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/api/drive/sync/status");
      setStatus(data);
      setError("");
    } catch (err) {
      console.error("Sync status failed:", err);
      setError("Could not load sync status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // The status only changes when a sync runs, so a slow poll is plenty.
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const runAction = async (label, request) => {
    setBusy(label);
    setMessage("");
    setError("");
    try {
      const { data } = await request();
      setMessage(
        data.status === "skipped"
          ? "A sync is already running."
          : `${label} finished: ${JSON.stringify(data)}`
      );
      await load();
    } catch (err) {
      console.error(`${label} failed:`, err);
      setError(err.response?.data?.error || `${label} failed.`);
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return <main className="admin-shell"><p>Loading sync status...</p></main>;
  }

  if (!status) {
    return (
      <main className="admin-shell">
        <p className="admin-error" role="alert">{error}</p>
      </main>
    );
  }

  const channelHealthy = status.channel.id && !status.channel.expired;

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <p className="admin-kicker">Google Drive</p>
          <h1>Sync Status</h1>
        </div>
        <div className="admin-actions">
          <button
            type="button"
            onClick={() => runAction("Full sync", () => client.post("/api/drive/sync/full"))}
            disabled={Boolean(busy)}
          >
            <Play size={15} strokeWidth={1.8} aria-hidden="true" />
            {busy === "Full sync" ? "Running..." : "Run full sync"}
          </button>
          <button
            type="button"
            onClick={() =>
              runAction("Incremental sync", () =>
                client.post("/api/drive/sync/incremental")
              )
            }
            disabled={Boolean(busy)}
          >
            <RefreshCw size={15} strokeWidth={1.8} aria-hidden="true" />
            {busy === "Incremental sync" ? "Running..." : "Sync changes"}
          </button>
          <button
            type="button"
            onClick={() => runAction("Watch", () => client.post("/api/drive/watch/start"))}
            disabled={Boolean(busy)}
          >
            <Radio size={15} strokeWidth={1.8} aria-hidden="true" />
            {busy === "Watch" ? "Registering..." : "Re-register webhook"}
          </button>
        </div>
      </header>

      {message && <p className="admin-notice">{message}</p>}
      {error && <p className="admin-error" role="alert">{error}</p>}

      <section
        className={`admin-banner ${channelHealthy ? "ok" : "warn"}`}
        aria-label="Webhook channel health"
      >
        {channelHealthy ? (
          <CheckCircle2 size={20} strokeWidth={1.8} aria-hidden="true" />
        ) : (
          <AlertTriangle size={20} strokeWidth={1.8} aria-hidden="true" />
        )}
        <div>
          <strong>
            {channelHealthy
              ? "Push notifications are active"
              : "Push notifications are not active"}
          </strong>
          <p>
            {channelHealthy
              ? `Channel expires ${formatTime(status.channel.expires_at)}. Drive channels last at most 24 hours and must be renewed by cron.`
              : "New files in Drive will only appear after the next scheduled full sweep. Re-register the webhook to restore live updates."}
          </p>
        </div>
      </section>

      <section className="admin-grid">
        <article className="admin-stat">
          <p className="admin-kicker">Live images</p>
          <strong>{status.images_live}</strong>
        </article>
        <article className="admin-stat">
          <p className="admin-kicker">Soft deleted</p>
          <strong>{status.images_soft_deleted}</strong>
          <span>Hidden from the portal, still on disk</span>
        </article>
        <article className="admin-stat">
          <p className="admin-kicker">Last full sync</p>
          <strong>{relative(status.last_full_sync_at) || "Never"}</strong>
          <span>{formatTime(status.last_full_sync_at)}</span>
        </article>
        <article className="admin-stat">
          <p className="admin-kicker">Last change replay</p>
          <strong>{relative(status.last_incremental_sync_at) || "Never"}</strong>
          <span>{formatTime(status.last_incremental_sync_at)}</span>
        </article>
      </section>

      <section className="admin-panel">
        <h2>Configuration</h2>
        <dl className="admin-definition-list">
          <div>
            <dt>Watched folder</dt>
            <dd><code>{status.root_folder_id}</code></dd>
          </div>
          <div>
            <dt>Webhook URL</dt>
            <dd><code>{status.webhook_url}</code></dd>
          </div>
          <div>
            <dt>Webhook secret set</dt>
            <dd>{status.webhook_configured ? "Yes" : "No - push cannot work"}</dd>
          </div>
          <div>
            <dt>Changes cursor</dt>
            <dd>{status.cursor_present ? "Present" : "Missing"}</dd>
          </div>
          <div>
            <dt>Channel ID</dt>
            <dd><code>{status.channel.id || "none"}</code></dd>
          </div>
        </dl>
      </section>

      <section className="admin-panel">
        <h2>Recent runs</h2>
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Added</th>
                <th>Updated</th>
                <th>Deleted</th>
                <th>Skipped</th>
                <th>Errors</th>
              </tr>
            </thead>
            <tbody>
              {status.recent_runs.map((run, index) => (
                <tr key={`${run.started_at}-${index}`}>
                  <td>{formatTime(run.started_at)}</td>
                  <td>{run.trigger}</td>
                  <td>
                    <span className={`pill pill-${run.status}`}>{run.status}</span>
                  </td>
                  <td>{run.added}</td>
                  <td>{run.updated}</td>
                  <td>{run.deleted}</td>
                  <td>{run.skipped}</td>
                  <td>{run.errors}</td>
                </tr>
              ))}
              {!status.recent_runs.length && (
                <tr>
                  <td colSpan={8} className="admin-empty-cell">
                    No syncs have run yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
